/**
 * v4.30 — useBranding() hook.
 *
 * Single source of truth for the operator's organization identity.
 * Used by every shell surface (Sidebar header block, Layout top bar,
 * Operational Base header, future surfaces). Reads from
 * `/api/operational-base/branding` and caches the response in memory
 * for the current authenticated session — keyed by access token, so
 * a super_admin switching organizations or impersonating another
 * tenant produces a fresh fetch (no cross-tenant bleed).
 *
 * Refetch triggers (no manual call needed by consumers):
 *   - Auth token changes (login / logout / context switch)
 *   - `ob:demo-mode-changed` event (Demo Mode lens flip)
 *   - `ob:branding-changed` event (admin uploaded a new logo)
 *
 * Theme awareness lives in the consumer (PortalBrandMark) — the hook
 * returns BOTH variants so a consumer can render the correct one
 * without re-querying when the theme toggles.
 */
import { useEffect, useState } from 'react';
import api from './api';
import { getToken } from './session';

/* Token-keyed in-memory cache. Map<token, payload>. Cleared on logout
 * (via the localStorage 'yulontap_token' key changing). */
const _cache = new Map();
let _activeToken = null;
const _subscribers = new Set();

const _read_token = () => {
    try {
        return getToken();
    } catch (e) {
        return null;
    }
};

const _emit = () => {
    for (const fn of _subscribers) {
        try { fn(); } catch (e) { /* calm */ }
    }
};

const _invalidate_and_refetch = () => {
    const tok = _read_token();
    _cache.delete(tok);
    _activeToken = tok;
    _fetch(tok);
};

const _fetch = (tok) => {
    if (!tok) {
        _cache.set(null, { loaded: true, payload: null });
        _emit();
        return;
    }
    api.get('/operational-base/branding')
        .then((res) => { _cache.set(tok, { loaded: true, payload: res.data || null }); })
        .catch(() => { _cache.set(tok, { loaded: true, payload: null }); })
        .finally(() => { _emit(); });
};

/* Wire global invalidation events once at module load. */
if (typeof window !== 'undefined' && !window.__yulBrandingHookWired) {
    window.__yulBrandingHookWired = true;
    window.addEventListener('ob:demo-mode-changed', _invalidate_and_refetch);
    window.addEventListener('ob:branding-changed', _invalidate_and_refetch);
    /* Detect token swaps (login / logout / context switch). */
    window.addEventListener('storage', (e) => {
        if (e.key === 'yulontap_token') _invalidate_and_refetch();
    });
}

/* ── Preview override (v4.30.1 — operator live preview) ────────────
 *
 * Lets an admin temporarily make every shell surface render as if it
 * belonged to another organization, without saving anything to the
 * DB and without switching auth tokens. Survives client-side route
 * navigation (cards/surveys/audit/etc.) because the override slot
 * lives at module scope, not in a per-route component. Exits cleanly
 * via clearPreviewOverride() or by hard refreshing the page.
 *
 * The override sits in front of the token-keyed cache: whenever it's
 * set, useBranding() returns it verbatim and ignores the normal
 * /branding response. */
let _preview_override = null; // { logo_url, logo_dark_url, organization, ... } | null

export const setPreviewOverride = (payload) => {
    _preview_override = payload && typeof payload === 'object' ? payload : null;
    _emit();
};

export const clearPreviewOverride = () => {
    if (_preview_override === null) return;
    _preview_override = null;
    _emit();
};

export const isPreviewOverrideActive = () => _preview_override !== null;
export const getPreviewOverride = () => _preview_override;

export const useBranding = () => {
    const [, setTick] = useState(0);

    useEffect(() => {
        const tok = _read_token();
        if (tok !== _activeToken) {
            _activeToken = tok;
            if (!_cache.has(tok)) _fetch(tok);
        } else if (!_cache.has(tok)) {
            _fetch(tok);
        }
        const sub = () => setTick((t) => t + 1);
        _subscribers.add(sub);
        return () => { _subscribers.delete(sub); };
    }, []);

    /* Override wins, but only while explicitly set. The mini-shell
     * preview card uses a parallel `override` prop on PortalBrandMark
     * (not this hook) so it can preview WITHOUT taking over every
     * surface — this slot is reserved for "Preview live shell". */
    if (_preview_override) {
        return { loaded: true, payload: _preview_override, preview: true };
    }

    const tok = _read_token();
    const slot = _cache.get(tok);
    if (!slot) return { loaded: false, payload: null, preview: false };
    return { loaded: true, payload: slot.payload, preview: false };
};

/* Public emitter so post-upload callsites can refresh every surface. */
export const notifyBrandingChanged = () => {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('ob:branding-changed'));
    }
};

export default useBranding;
